// Version history for a hosted site with rollback to an older version (confirmed in a dialog)

import { useState } from 'react'
import { toast } from 'sonner'
import { History, RotateCcw } from 'lucide-react'
import { useRollbackSite, useSiteVersions, type HostedSite, type SiteVersion } from '../../hooks/use-hosted-sites'
import { ApiError } from '../../lib/api-client'
import { useAppStore } from '../../stores/app-store'
import { useT } from '../../i18n'
import { cn } from '../../lib/utils'
import { Modal } from '../ui/modal'
import { badgeClass, buttonPrimaryClass, buttonSecondaryClass, errorTextClass } from '../ui/styles'
import { formatBytes, formatDate, liveVersionId } from './utils'

export function SiteVersions({ site }: { site: HostedSite }) {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const { data, isLoading, isError, refetch } = useSiteVersions(site.id)
  const rollback = useRollbackSite()
  const [target, setTarget] = useState<SiteVersion | null>(null)

  const versions = data?.versions ?? []
  const liveId = liveVersionId(versions, site.version)

  const confirmRollback = async () => {
    if (!target) return
    try {
      const result = await rollback.mutateAsync({ siteId: site.id, versionId: target.id })
      toast.success(t('sites.versions.rolledBack', { version: result.version }))
      setTarget(null)
    } catch (err) {
      const message = err instanceof ApiError && err.status === 409 ? t('sites.versions.conflict') : err instanceof Error ? err.message : String(err)
      toast.error(t('sites.versions.rollbackFailed', { message }))
      if (err instanceof ApiError && err.status === 409) void refetch()
    }
  }

  return (
    <div className="space-y-2">
      <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-fg-subtle">
        <History className="h-3.5 w-3.5" aria-hidden="true" />
        {t('sites.versions.title')}
      </h4>
      {isLoading ? (
        <p className="text-xs text-fg-subtle">{t('common.loading')}</p>
      ) : isError ? (
        <div role="alert" className={cn(errorTextClass, 'text-xs')}>
          {t('sites.versions.loadError')}{' '}
          <button type="button" onClick={() => void refetch()} className="cursor-pointer font-medium underline underline-offset-2">
            {t('common.retry')}
          </button>
        </div>
      ) : versions.length === 0 ? (
        <p className="text-xs text-fg-subtle">{t('sites.versions.empty')}</p>
      ) : (
        <ul aria-label={t('sites.versions.listLabel', { name: site.filename })} className="divide-y divide-border rounded-md border border-border">
          {versions.map((v) => {
            const isLive = v.id === liveId
            return (
              <li key={v.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 text-sm text-fg">
                    {t('sites.versions.version', { version: v.version })}
                    {isLive && <span className={cn(badgeClass, 'border-success/30 bg-success-soft text-success')}>{t('sites.versions.live')}</span>}
                  </p>
                  <p className="text-xs text-fg-subtle">
                    {formatBytes(v.totalSizeBytes)} · {t(v.fileCount === 1 ? 'sites.filesOne' : 'sites.files', { count: v.fileCount })} ·{' '}
                    {formatDate(v.createdAt, locale)}
                  </p>
                  {v.changelog && <p className="text-xs text-fg-muted">{v.changelog}</p>}
                </div>
                <button
                  type="button"
                  onClick={() => setTarget(v)}
                  disabled={isLive || rollback.isPending}
                  aria-label={t('sites.versions.rollbackNamed', { version: v.version })}
                  className={cn(buttonSecondaryClass, 'min-h-8 py-1 text-xs')}
                >
                  <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('sites.versions.rollback')}
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {target && (
        <Modal title={t('sites.versions.rollbackTitle')} onClose={() => setTarget(null)}>
          <div className="space-y-4">
            <p className="text-sm text-fg-muted">{t('sites.versions.rollbackBody', { version: target.version, name: site.filename })}</p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setTarget(null)} className={buttonSecondaryClass}>
                {t('common.cancel')}
              </button>
              <button type="button" data-autofocus onClick={() => void confirmRollback()} disabled={rollback.isPending} className={buttonPrimaryClass}>
                {rollback.isPending ? t('sites.versions.rollingBack') : t('sites.versions.rollback')}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
